/**
 * The relay reboot (M3 plan R19, §2.9 flow 10, spec §6.3): defend the relay for the region's time while raider
 * waves (and, near a tetherstone vein, guardians) besiege it; allied patrols may help when the owner patrols.
 *
 * - **Start:** status `reboot`; a siege objective at the repair port (inset into the rim) with the relay's colliders
 *   remapped to it; the spawn schedule; allies (by chance); `REV.rebootStart`.
 * - **Per tick** (`stepReboot`, allocation-free apart from the rare branches): the timer, the objective's hull for the
 *   HUD, a remap when the structures re-streamed, the due spawns (`REV.rebootWave`), then the outcome checks.
 * - **Outcomes:** destroyed (hull 0: parts lost, `attempts + 1`), aborted (the player died, went beyond
 *   `leaveAbortM` or left the system: parts kept) and online (the timer ran out: `relinkedAt`, a pending sponsor,
 *   the far system rumoured, an autosave). Each removes the objective, restores the colliders and emits
 *   `REV.rebootEnd`. Surviving raiders keep fighting and despawn by the transient rule.
 */
import { SIM_DT } from '../core/loop'
import { logger } from '../core/logger'
import { nextFloat } from '../core/rng'
import { emitReach, REV } from '../reach/events'
import type { ReachCore } from '../reach/runtime/types'
import { RELAY_STATE } from '../reach/session/structures'
import { clearDelivered, ensureRelayState, markRumoured } from '../reach/state'
import { galaxyIndex } from '../reach/state/galaxyIndex'
import { addGrudge } from '../combat/hostility'
import { GUARDIAN_TEAM_ID } from '../charter/hostilityWriter'
import { PLAYER_TEAM, RAIDER_TEAM, type Sim } from '../sim'
import { Hull, Life, Pos64, Team } from '../world/components'
import { persistable, type RelinkContext } from './context'
import { createSiegeObjective, remapStructureColliders, removeSiegeObjective, restoreStructureColliders } from './objective'
import { ownerPatrols, rebootSchedule, veinWithinHops } from './rules'

/** `REV.rebootEnd` outcome codes (its `b` field). */
export const REBOOT_END = { online: 0, destroyed: 1, aborted: 2 } as const
export type RebootEnd = (typeof REBOOT_END)[keyof typeof REBOOT_END]

/** Maps the relay's streamed colliders to the objective again (after a re-stream; rare). */
function remap(core: ReachCore, sim: Sim, ctx: RelinkContext, eid: number): void {
  const run = ctx.run
  restoreStructureColliders(sim.physics, run.handles, eid)
  run.handles = []
  const site = ctx.slots.sites[run.slot]
  if (site !== undefined) remapStructureColliders(sim.physics, site.position[0], site.position[1], site.position[2], eid, run.handles)
  run.structuresVersion = core.session.structuresVersion
}

/**
 * Starts the reboot of the relay at `slot` of the current system. The caller has checked everything (offline,
 * scanned, every part delivered, the player at the port, no reboot running). Rare: allocates.
 */
export function startReboot(core: ReachCore, sim: Sim, ctx: RelinkContext, slot: number): void {
  const slots = ctx.slots
  const layout = slots.layout
  const site = slots.sites[slot]
  if (layout === null || site === undefined) throw new Error(`relink: no relay at slot ${slot}`)
  const cfg = ctx.cfg.reboot
  ensureRelayState(core.state, core.galaxy, site.relayId).status = 'reboot'
  core.session.setStructureState('relay', site.index, RELAY_STATE.reboot)
  const p = sim.playerEid
  const team = p >= 0 ? Team.id[p] : PLAYER_TEAM
  const eid = createSiegeObjective(sim, slots.objective[slot * 3], slots.objective[slot * 3 + 1], slots.objective[slot * 3 + 2], team, cfg.hull, cfg.siegeWeight)
  const run = ctx.run
  run.slot = slot
  run.relayId = site.relayId
  run.next = 0
  run.handles = []
  run.guardianGroup = -1
  remap(core, sim, ctx, eid)
  const duration = cfg.seconds[layout.region]
  const guardians = veinWithinHops(core.galaxy, layout.systemId, cfg.guardians.hops)
  run.schedule = rebootSchedule(layout.region, duration, guardians, ctx.cfg)
  const r = core.relink
  r.relayIndex = site.index
  r.phase = 'reboot'
  r.t = 0
  r.duration = duration
  r.hull = cfg.hull
  r.hullMax = cfg.hull
  r.wave = 0
  r.waves = run.schedule.length
  r.objectiveEid = eid
  if (ownerPatrols(layout.owner, ctx.factions) && cfg.allies.ships.length > 0 && nextFloat(core.rng) < cfg.allies.chance) {
    core.session.spawnGroup({
      ships: cfg.allies.ships,
      center: site.position,
      facing: site.inward,
      radiusM: cfg.allies.radiusM,
      team,
      kind: 'ally',
      despawnM: cfg.despawnM,
    })
  }
  emitReach(core.events, REV.rebootStart, site.index, 0, site.position[0], site.position[1], site.position[2], 0)
  logger.info(`relink: reboot of ${site.relayId} started (${duration} s, ${run.schedule.length} waves${guardians ? ', guardians' : ''})`)
}

/** Spawns the next scheduled wave around the relay (rare: allocates). */
function spawnNext(core: ReachCore, ctx: RelinkContext): void {
  const run = ctx.run
  const entry = run.schedule[run.next]
  run.next++
  const site = ctx.slots.sites[run.slot]
  if (entry === undefined || site === undefined) return
  const cfg = ctx.cfg.reboot
  // M5 (plan C14): guardians fly on the guardian cell's team, raider waves on the raider team.
  const guardian = entry.kind === 'guardian'
  const group = core.session.spawnGroup({
    ships: entry.ships,
    center: site.position,
    facing: site.inward,
    radiusM: cfg.spawnRadiusM,
    team: guardian ? GUARDIAN_TEAM_ID : RAIDER_TEAM,
    kind: entry.kind,
    despawnM: cfg.despawnM,
  })
  if (guardian) run.guardianGroup = group
  core.relink.wave++
  emitReach(core.events, REV.rebootWave, site.index, core.relink.wave, site.position[0], site.position[1], site.position[2], 0)
}

/** One tick of a running reboot; nothing when idle. Allocation-free apart from spawns, remaps and the outcome. */
export function stepReboot(core: ReachCore, sim: Sim, ctx: RelinkContext): void {
  const r = core.relink
  if (r.phase !== 'reboot') return
  const run = ctx.run
  const site = ctx.slots.sites[run.slot]
  if (site === undefined || site.relayId !== run.relayId) {
    endReboot(core, sim, ctx, REBOOT_END.aborted)
    return
  }
  const o = r.objectiveEid
  r.t += SIM_DT
  r.hull = Hull.value[o]
  if (run.structuresVersion !== core.session.structuresVersion) remap(core, sim, ctx, o)
  while (run.next < run.schedule.length && run.schedule[run.next].atS <= r.t) spawnNext(core, ctx)
  // The guardian cell defends the vein: while its reboot wave lives it holds a grudge against the relay's side
  // (refreshed each tick; it lapses by local.json grudgeS once the wave is gone).
  if (run.guardianGroup >= 0 && core.session.groupAlive(run.guardianGroup) > 0) addGrudge(GUARDIAN_TEAM_ID, Team.id[o], sim.time)
  const p = sim.playerEid
  if (r.hull <= 0) endReboot(core, sim, ctx, REBOOT_END.destroyed)
  else if (p < 0 || Life.alive[p] !== 1) endReboot(core, sim, ctx, REBOOT_END.aborted)
  else {
    // Read here, not passed to a helper: float arguments to a call that is not inlined are boxed every tick.
    const at = site.position
    const dx = Pos64.x[p] - at[0]
    const dy = Pos64.y[p] - at[1]
    const dz = Pos64.z[p] - at[2]
    const leave = ctx.cfg.leaveAbortM
    if (dx * dx + dy * dy + dz * dz > leave * leave) endReboot(core, sim, ctx, REBOOT_END.aborted)
    else if (r.t >= r.duration) endReboot(core, sim, ctx, REBOOT_END.online)
  }
}

/** The relay's side of an outcome: its status, deliveries, attempts, structure look, sponsor and far system. */
function settleRelay(core: ReachCore, relayId: string, relayIndex: number, far: string | undefined, outcome: RebootEnd): void {
  const state = core.state
  const rs = ensureRelayState(state, core.galaxy, relayId)
  if (outcome === REBOOT_END.online) {
    rs.status = 'online'
    rs.relinkedAt = state.gameTime
    rs.sponsorPending = true
    clearDelivered(state, core.galaxy, relayId)
    core.session.setStructureState('relay', relayIndex, RELAY_STATE.online)
    core.uiRequest = { kind: 'sponsor', relayId }
    if (far !== undefined && markRumoured(state, far)) {
      emitReach(core.events, REV.systemRumoured, galaxyIndex(core.galaxy).systems.get(far)?.index ?? -1, 0, 0, 0, 0, 0)
    }
    return
  }
  rs.status = 'offline'
  if (outcome === REBOOT_END.destroyed) {
    clearDelivered(state, core.galaxy, relayId)
    rs.attempts++
  }
  core.session.setStructureState('relay', relayIndex, RELAY_STATE.offline)
}

const END_NAMES: Readonly<Record<RebootEnd, string>> = { 0: 'online', 1: 'destroyed', 2: 'aborted' }

/** Ends a running reboot with `outcome` (a no-op when idle). Rare: allocates. */
export function endReboot(core: ReachCore, sim: Sim, ctx: RelinkContext, outcome: RebootEnd): void {
  const r = core.relink
  if (r.phase !== 'reboot') return
  const run = ctx.run
  const relayId = run.relayId
  const relayIndex = r.relayIndex
  const site = ctx.slots.sites[run.slot]
  restoreStructureColliders(sim.physics, run.handles, r.objectiveEid)
  removeSiegeObjective(sim, r.objectiveEid)
  if (galaxyIndex(core.galaxy).relays.has(relayId)) {
    settleRelay(core, relayId, relayIndex, site?.otherSystemId, outcome)
    const at = site?.position ?? [0, 0, 0]
    emitReach(core.events, REV.rebootEnd, relayIndex, outcome, at[0], at[1], at[2], 0)
    logger.info(`relink: reboot of ${relayId} ended (${END_NAMES[outcome]})`)
  } else {
    logger.warn(`relink: a reboot of an unknown relay "${relayId}" was reset`)
  }

  r.relayIndex = -1
  r.phase = 'idle'
  r.t = 0
  r.duration = 0
  r.hull = 0
  r.hullMax = 0
  r.wave = 0
  r.waves = 0
  r.objectiveEid = -1
  run.slot = -1
  run.relayId = ''
  run.schedule = []
  run.next = 0
  run.handles = []
  run.structuresVersion = -1
  if (outcome === REBOOT_END.online && persistable(core)) core.persist('auto')
}
